import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Dashboard,
  SmartToy,
  MenuBook,
  Settings,
  Help,
  Policy,
  Description,
  Payment,
  Extension,
  ChevronLeft,
  ChevronRight,
  ManageAccounts,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../../hooks/useTranslation';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import useAuth from '../../../hooks/useAuth';
import { useSubscription } from '../../../contexes/SubscriptionProvider';
import {
  canPerformAction,
  PERMISSIONS,
  Permission,
} from '../../../utils/permissions';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { shouldShowUpgradeCta } from '../../../helpers/billingPlanSwitchUi';
import StyledButton from '../Button/StyledButton';
import { useIntegrationsAccess } from '../../../hooks/useIntegrationsAccess';
import logoWhite from '../../../assets/logo_white.svg';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Sidebar = ({
  isOpen,
  onClose,
  isMobile = false,
  isCollapsed = false,
  onToggleCollapse,
}: SidebarProps): JSX.Element => {
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { subscription, company } = useSubscription();
  const canManageBilling = Boolean(
    currentUser && canPerformAction(currentUser, PERMISSIONS.MANAGE_BILLING)
  );
  const { track } = useAnalytics();
  const { canUse: showIntegrationsNav } = useIntegrationsAccess();
  const showUpgradeCta = shouldShowUpgradeCta(company, subscription);
  const planSectionPadding = 0.5;
  const navSectionTopPadding = 0.5;

  type MenuItem = {
    text: string;
    path: string;
    icon: React.ReactElement;
    isExternal?: boolean;
    permission?: Permission;
  };

  const allMenuItems: MenuItem[] = [
    {
      text: t('Dashboard'),
      path: '/dashboard',
      icon: <Dashboard />,
      permission: PERMISSIONS.VIEW_AGENTS_DASHBOARD,
    },
    {
      text: t('Agents'),
      path: '/agents',
      icon: <SmartToy />,
      permission: PERMISSIONS.VIEW_AGENTS_DASHBOARD,
    },
    {
      text: t('Knowledges'),
      path: '/knowledges',
      icon: <MenuBook />,
      permission: PERMISSIONS.MANAGE_KNOWLEDGE,
    },
    ...(showIntegrationsNav
      ? [
          {
            text: t('Integrations'),
            path: '/integrations',
            icon: <Extension />,
          },
        ]
      : []),
    { text: t('Settings'), path: '/settings', icon: <Settings /> },
    {
      text: t('User Management'),
      path: '/user-management',
      icon: <ManageAccounts />,
      permission: PERMISSIONS.MANAGE_USERS,
    },
    {
      text: t('Usage'),
      path: '/usage',
      icon: <BarChart />,
      permission: PERMISSIONS.MANAGE_BILLING as Permission,
    },
    {
      text: t('Billing'),
      path: '/billing',
      icon: <Payment />,
      permission: PERMISSIONS.MANAGE_BILLING,
    },
    { text: t('Help'), path: '/help', icon: <Help /> },
  ];

  const menuItems = allMenuItems.filter(
    item =>
      !item.permission ||
      (currentUser && canPerformAction(currentUser, item.permission))
  );

  const footerItems = [
    { text: t('Privacy Policy'), path: '/privacy-policy', icon: <Policy /> },
    {
      text: t('Terms of Service'),
      path: '/terms-of-service',
      icon: <Description />,
    },
  ];

  const drawerWidth = 280;
  const collapsedWidth = 92;
  const collapsed = !isMobile && isCollapsed;
  const effectiveWidth = isMobile
    ? drawerWidth
    : collapsed
      ? collapsedWidth
      : drawerWidth;

  const sidebarContent = (
    <Box
      sx={{
        width: effectiveWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1E40FF',
        color: '#FFFFFF',
        borderRight: '1px solid rgba(255,255,255,0.2)',
        borderRadius: isMobile ? 0 : '0 22px 22px 0',
        overflow: 'hidden',
        transition: theme.transitions.create(['width'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      {/* Logo section */}
      <Box
        sx={{
          p: collapsed ? 1.5 : 2.5,
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <Link to="/dashboard" style={{ textDecoration: 'none' }}>
          <Box
            component="img"
            src={logoWhite}
            alt={t('Elora logo')}
            sx={{
              width: collapsed ? 36 : 128,
              height: 'auto',
              transition: theme.transitions.create(['width'], {
                duration: theme.transitions.duration.shorter,
              }),
            }}
          />
        </Link>
      </Box>
      {/* Plan and usage information */}
      {!collapsed && currentUser && showUpgradeCta && canManageBilling && (
        <Box sx={{ p: planSectionPadding }}>
          <Link
            to="/pricing"
            style={{ textDecoration: 'none' }}
            onClick={() => {
              track(AnalyticsEvents.UPGRADE_BUTTON_CLICKED);
              if (isMobile) onClose();
            }}
          >
            <StyledButton
              variant="contained"
              color="success"
              size="small"
              fullWidth
            >
              {t('Upgrade')}
            </StyledButton>
          </Link>
        </Box>
      )}

      {/* Main navigation items */}
      <Box sx={{ flex: 1, pt: navSectionTopPadding + 1 }}>
        <List>
          {menuItems.map(item => (
            <ListItem key={item.path} disablePadding>
              <Tooltip title={collapsed ? item.text : ''} placement="right">
                <ListItemButton
                  component={item.isExternal ? 'a' : Link}
                  {...(item.isExternal
                    ? {
                        href: item.path,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        onClick: () => {
                          track(
                            AnalyticsEvents.BILLING_MANAGEMENT_BUTTON_CLICKED
                          );
                          if (isMobile) onClose();
                        },
                      }
                    : {
                        to: item.path,
                        onClick: isMobile ? onClose : undefined,
                      })}
                  selected={!item.isExternal && location.pathname === item.path}
                  sx={{
                    mx: 1.25,
                    borderRadius: 2.5,
                    minHeight: 44,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    color: 'rgba(255,255,255,0.92)',
                    '& .MuiListItemIcon-root': {
                      color: 'inherit',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255,255,255,0.18)',
                      color: '#FFFFFF',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.26)',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.14)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 40,
                      mr: collapsed ? 0 : 0.5,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.98rem',
                          fontWeight:
                            !item.isExternal && location.pathname === item.path
                              ? 700
                              : 500,
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Footer section with language selector and legal links */}
      <Box sx={{ mt: 'auto' }}>
        {/* Legal links */}
        <List sx={{ py: 1 }}>
          {footerItems.map(item => (
            <ListItem key={item.path} disablePadding>
              <Tooltip title={collapsed ? item.text : ''} placement="right">
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={isMobile ? onClose : undefined}
                  selected={location.pathname === item.path}
                  sx={{
                    mx: 1.25,
                    borderRadius: 2.5,
                    minHeight: 38,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    color: '#FFFFFF',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255,255,255,0.18)',
                      color: '#FFFFFF',
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.14)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 40,
                      color: 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: '0.85rem',
                          fontWeight:
                            location.pathname === item.path ? 600 : 400,
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        {/* Language selector */}
        <Box sx={{ p: 2, display: collapsed ? 'none' : 'block' }}>
          <LanguageSelector forceWhiteText />
        </Box>

        {/* Powered by text */}
        <Box
          sx={{
            px: 2,
            py: 2,
            mx: 1,
            textAlign: 'left',
            color: '#FFFFFF',
            fontSize: '0.75rem',
            display: collapsed ? 'none' : 'block',
          }}
        >
          {t('Elora powered by Gen AI')}
        </Box>
      </Box>
      {!isMobile && onToggleCollapse ? (
        <IconButton
          onClick={onToggleCollapse}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 32,
            height: 60,
            borderRadius: 3,
            p: 0,
            bgcolor: '#FFFFFF',
            color: '#1E40FF',
            boxShadow: theme.shadows[2],
            '&:hover': {
              bgcolor: '#F4F7FF',
            },
          }}
          aria-label={collapsed ? t('Expand sidebar') : t('Collapse sidebar')}
        >
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      ) : null}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: effectiveWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        width: effectiveWidth,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1E40FF',
        borderRight: '1px solid rgba(255,255,255,0.2)',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '0 22px 22px 0',
        transition: theme.transitions.create(['width'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default Sidebar;
